/**
 * La marca del negocio: su logo y el color de los detalles.
 *
 * Dos decisiones de esta pantalla que vale explicar:
 *
 * 1. La vista previa del logo va sobre un DAMERO gris, no sobre blanco. Si un
 *    PNG viene con fondo transparente y lo enseñamos sobre blanco, se ve
 *    idéntico a uno con fondo blanco — y el dueño no puede saber si su logo
 *    quedó bien hasta que lo ve en la barra oscura o en un correo. El damero
 *    delata la transparencia al instante.
 *
 * 2. Los colores son botones de una lista, no un selector libre de color. Es lo
 *    que pidió el dueño y además protege al cliente: cada color de la lista
 *    tiene contraste suficiente con texto blanco encima. Un selector libre
 *    permite amarillo flúor sobre blanco y el sistema queda ilegible.
 */
import { useEffect, useState, useCallback } from 'react';
import api, { formatApiError } from '@/lib/api';
import { olvidarMarca } from '@/lib/marca';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { urlDeLogo } from '@/lib/marca';
import { Palette, Upload, Trash2, Check, Loader2, Image as Imagen } from 'lucide-react';

const DAMERO = {
  backgroundImage:
    'linear-gradient(45deg, #E2E8F0 25%, transparent 25%), linear-gradient(-45deg, #E2E8F0 25%, transparent 25%), ' +
    'linear-gradient(45deg, transparent 75%, #E2E8F0 75%), linear-gradient(-45deg, transparent 75%, #E2E8F0 75%)',
  backgroundSize: '14px 14px',
  backgroundPosition: '0 0, 0 7px, 7px -7px, -7px 0px',
  backgroundColor: '#F8FAFC',
};

export default function MarcaDelNegocio() {
  const [marca, setMarca] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [subiendo, setSubiendo] = useState(null);
  const [guardando, setGuardando] = useState(false);

  const cargar = useCallback(() => {
    api.get('/tenant/branding')
      .then(r => setMarca(r.data))
      .catch(e => toast.error(formatApiError(e)))
      .finally(() => setCargando(false));
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const subir = async (variante, archivo) => {
    if (!archivo) return;
    setSubiendo(variante);
    const cuerpo = new FormData();
    cuerpo.append('archivo', archivo);
    try {
      // Sin Content-Type a mano: el cliente `api` pone json en toda petición y
      // eso rompe el multipart. Poniéndolo en undefined, el navegador escribe
      // el suyo con el `boundary` que hace falta.
      const r = await api.post(`/tenant/branding/logo/${variante}`, cuerpo,
        { headers: { 'Content-Type': undefined } });
      setMarca(r.data);
      olvidarMarca();
      toast.success(r.data?.transparencia_posible
        ? 'Logo guardado. Si tenía fondo transparente, se respetó tal cual.'
        : 'Logo guardado.');
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubiendo(null);
    }
  };

  const quitar = async (variante) => {
    try {
      const r = await api.delete(`/tenant/branding/logo/${variante}`);
      setMarca(r.data);
      olvidarMarca();
      toast.success('Logo quitado. Vuelve a aparecer el nombre del negocio.');
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const elegirColor = async (campo, clave) => {
    setGuardando(true);
    try {
      const r = await api.put('/tenant/branding', { [campo]: clave });
      setMarca(r.data);
      olvidarMarca();
      toast.success('Color guardado. Ya se aplica en toda la pantalla.');
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setGuardando(false); }
  };

  if (cargando) {
    return (
      <div className="bg-white border border-[#E2E8F0] rounded-xl p-6 flex items-center gap-2 text-sm text-[#64748B]">
        <Loader2 className="w-4 h-4 animate-spin" /> Cargando tu marca…
      </div>
    );
  }
  if (!marca) return null;

  const puede = marca.puede_editar;

  return (
    <div className="bg-white border border-[#E2E8F0] rounded-xl p-6 space-y-6" data-testid="marca-del-negocio">
      <div>
        <h3 className="text-lg font-semibold text-[#0F172A] flex items-center gap-2">
          <Palette className="w-5 h-5" /> Tu marca
        </h3>
        <p className="text-sm text-[#64748B] mt-1">
          Sube el logo de tu negocio y elige el color de los detalles. El logo aparece
          arriba a la izquierda en lugar del nombre «CavernicolApp» y también en los
          correos que envías a tus clientes. Los cambios se ven al instante, sin recargar.
        </p>
      </div>

      {!puede && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-900">
          Solo el dueño o un administrador pueden cambiar la marca, porque la ve todo
          el equipo. Aquí la puedes consultar.
        </div>
      )}

      {/* ── los dos logos ───────────────────────────────────────── */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <RecuadroLogo
          titulo="Logo cuadrado"
          ayuda="Aparece en el icono de la pestaña del navegador, junto a tu rol en la barra lateral, y en los correos si no subes el horizontal. Ideal más o menos 512 × 512 píxeles."
          variante="square"
          tiene={marca.tiene_logo_cuadrado}
          tenantId={marca.tenant_id}
          sello={marca.sello_cuadrado}
          alto="h-28 w-28"
          puede={puede}
          subiendo={subiendo === 'square'}
          onSubir={subir}
          onQuitar={quitar}
        />
        <RecuadroLogo
          titulo="Logo horizontal"
          ayuda="Aparece arriba a la izquierda en lugar del nombre «CavernicolApp», y encabeza los correos que envías. Ideal más o menos 600 × 160 píxeles."
          variante="wide"
          tiene={marca.tiene_logo_ancho}
          tenantId={marca.tenant_id}
          sello={marca.sello_ancho}
          alto="h-16 w-full"
          puede={puede}
          subiendo={subiendo === 'wide'}
          onSubir={subir}
          onQuitar={quitar}
        />
      </div>

      <p className="text-xs text-[#64748B] bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg p-3">
        <strong>PNG, JPG o WEBP, hasta {marca.maximo_mb} MB.</strong> Si tu logo es un PNG con
        fondo transparente, se guarda tal cual: no le ponemos fondo ni lo recortamos.
        Los archivos SVG no se aceptan por seguridad — expórtalo como PNG.
      </p>

      {/* ── los colores ─────────────────────────────────────────── */}
      <div className="border-t border-[#E2E8F0] pt-5 space-y-5">
        <Paleta
          titulo="Color de los detalles (fondo claro)"
          ayuda="Botones, enlaces y datos destacados sobre fondo claro. Cada color trae su texto legible encima (blanco o casi negro, según el tono)."
          opciones={marca.paleta_clara}
          elegido={marca.acento}
          puede={puede && !guardando}
          onElegir={(c) => elegirColor('acento', c)}
          muestraOn
        />
        <Paleta
          titulo="Color de los detalles (modo oscuro)"
          ayuda="Solo tonos claros o pasteles: sobre fondo oscuro un color oscuro desaparece. El texto de los botones se calcula para no perderse (blanco, hielo o tinta según el color)."
          opciones={marca.paleta_oscura}
          elegido={marca.acento_oscuro}
          puede={puede && !guardando}
          onElegir={(c) => elegirColor('acento_oscuro', c)}
          muestraOn
        />
      </div>
    </div>
  );
}

function RecuadroLogo({ titulo, ayuda, variante, tiene, tenantId, sello, alto, puede, subiendo, onSubir, onQuitar }) {
  const src = urlDeLogo(tenantId, variante, sello);
  return (
    <div className="border border-[#E2E8F0] rounded-lg p-4 space-y-3">
      <div>
        <p className="text-sm font-semibold text-[#0F172A]">{titulo}</p>
        <p className="text-xs text-[#64748B] mt-0.5">{ayuda}</p>
      </div>
      <div className="rounded-lg border border-[#E2E8F0] p-3 flex items-center justify-center" style={DAMERO}>
        {tiene ? (
          <img src={src} alt={titulo} className={`${alto} object-contain`} data-testid={`logo-${variante}`} />
        ) : (
          <div className={`${alto} flex items-center justify-center text-[#94A3B8] text-xs gap-1.5`}>
            <Imagen className="w-4 h-4" /> sin logo
          </div>
        )}
      </div>
      {/* El cuadriculado gris de atrás no es decoración: es lo que permite ver
          si el fondo del logo es transparente o blanco. */}
      <p className="text-[11px] text-[#94A3B8]">
        El cuadriculado gris es el fondo de la vista previa, no parte de tu logo.
      </p>
      {puede && (
        <div className="flex items-center gap-2">
          <label className="flex-1">
            <input type="file" accept=".png,.jpg,.jpeg,.webp" className="hidden"
              onChange={(e) => { onSubir(variante, e.target.files?.[0]); e.target.value = ''; }} />
            <span className="w-full inline-flex items-center justify-center gap-1.5 h-9 px-3 rounded-md border border-[#E2E8F0] text-sm font-medium text-[#0F172A] hover:bg-[#F8FAFC] cursor-pointer">
              {subiendo ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              {tiene ? 'Cambiar' : 'Subir'}
            </span>
          </label>
          {tiene && (
            <Button variant="ghost" size="sm" onClick={() => onQuitar(variante)}
              className="text-red-600 hover:text-red-700 hover:bg-red-50">
              <Trash2 className="w-4 h-4" />
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

function Paleta({ titulo, ayuda, opciones, elegido, puede, onElegir, muestraOn }) {
  return (
    <div>
      <p className="text-sm font-semibold text-[#0F172A]">{titulo}</p>
      <p className="text-xs text-[#64748B] mt-0.5 mb-3">{ayuda}</p>
      <div className="flex flex-wrap gap-2">
        {(opciones || []).map(o => (
          <button key={o.clave} type="button" disabled={!puede}
            onClick={() => onElegir(o.clave)}
            data-testid={`color-${o.clave}`}
            title={o.etiqueta}
            className={`flex items-center gap-2 pl-1.5 pr-3 py-1.5 rounded-full border text-xs font-medium transition-colors ${
              elegido === o.clave
                ? 'border-[#0F172A] text-[#0F172A] bg-[#F8FAFC]'
                : 'border-[#E2E8F0] text-[#64748B] hover:border-[#94A3B8]'
            } ${puede ? '' : 'opacity-60 cursor-not-allowed'}`}>
            <span
              className="w-6 h-6 rounded-full flex items-center justify-center shrink-0 text-[10px] font-bold"
              style={{ backgroundColor: o.hex, color: o.on || '#FFFFFF' }}
            >
              {elegido === o.clave && <Check className="w-3.5 h-3.5" style={{ color: o.on || '#FFFFFF' }} />}
            </span>
            {o.etiqueta}
          </button>
        ))}
      </div>
      {muestraOn && elegido && (() => {
        const sel = (opciones || []).find(x => x.clave === elegido);
        if (!sel) return null;
        return (
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <span className="text-[11px] text-[#64748B]">Vista previa del botón:</span>
            <span
              className="inline-flex items-center h-8 px-3 rounded-md text-xs font-semibold"
              style={{ backgroundColor: sel.hex, color: sel.on || '#FFFFFF' }}
              data-testid="marca-preview-boton"
            >
              Guardar cambios
            </span>
            <span
              className="inline-flex items-center h-8 px-3 rounded-md text-xs font-semibold border"
              style={{ borderColor: sel.hex, color: sel.ink || sel.hex }}
            >
              Enlace / outline
            </span>
          </div>
        );
      })()}
    </div>
  );
}
